import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { PlusCircle, BookOpen, Clock, BarChart2, Layers } from 'lucide-react';

// ----------------------------------------------------------------
// Stat card
// ----------------------------------------------------------------
interface StatCardProps {
  label: string;
  value: string;
  icon: React.ElementType;
  to?: string;
}

function StatCard({ label, value, icon: Icon, to }: StatCardProps) {
  const content = (
    <div
      className="panel p-4 space-y-3 transition-all duration-200"
      style={{
        cursor: to ? 'pointer' : 'default',
      }}
      onMouseEnter={(e) => {
        if (to) {
          (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border-hi)';
        }
      }}
      onMouseLeave={(e) => {
        if (to) {
          (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border)';
        }
      }}
    >
      <div className="flex items-center justify-between">
        <span className="label-tag">{label}</span>
        <Icon size={14} style={{ color: 'var(--color-accent)' }} />
      </div>
      <p
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: '2rem',
          color: 'var(--color-text)',
          lineHeight: 1,
        }}
      >
        {value}
      </p>
    </div>
  );

  return to ? <Link to={to}>{content}</Link> : <div>{content}</div>;
}

// ----------------------------------------------------------------
// Homepage — dashboard landing view
// ----------------------------------------------------------------
export default function Homepage() {
  const { user } = useUser();
  const firstName = user?.firstName || 'Maker';

  const stats: StatCardProps[] = [
    { label: 'Skills Tracked', value: '0', icon: BookOpen, to: '/app/skills' },
    { label: 'Practice Sessions', value: '0', icon: Clock, to: '/app/timer' },
    { label: 'Hours This Week', value: '0h', icon: BarChart2 },
    { label: 'Active Projects', value: '0', icon: Layers },
  ];

  return (
    <div className="space-y-6">

      {/* ── Header / Title Block ── */}
      <div
        className="title-block flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <p className="spec-heading mb-2">Dashboard</p>
          <h1
            style={{
              fontFamily: 'var(--font-heading)',
              color: 'var(--color-text)',
              fontSize: '1.5rem',
              letterSpacing: '0.04em',
            }}
          >
            Welcome back, {firstName}.
          </h1>
          <p
            className="mt-1 text-sm"
            style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}
          >
            Your workspace is ready. Start tracking skills, sessions, and progress.
          </p>
        </div>
        <Link to="/app/skills/new" className="btn-primary shrink-0 self-start sm:self-center">
          <PlusCircle size={14} />
          New Skill
        </Link>
      </div>

      {/* ── Stats Grid ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      {/* ── Recent Activity ── */}
      <div className="panel overflow-hidden">
        <div
          className="px-5 py-3 flex items-center justify-between"
          style={{ borderBottom: '1px solid var(--color-border)' }}
        >
          <p className="spec-heading">Recent Activity</p>
          <span className="badge">0 entries</span>
        </div>
        <div className="px-5 py-14 flex flex-col items-center gap-4 text-center">
          <span className="label-tag">No activity yet</span>
          <p
            className="text-sm max-w-sm"
            style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}
          >
            Add your first skill and OwensRoot will track every practice session,
            milestone, and project update right here.
          </p>
          <Link to="/app/skills/new" className="btn-ghost mt-2">
            <PlusCircle size={14} />
            Add First Skill
          </Link>
        </div>
      </div>

      {/* ── Quick Actions ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          {
            to: '/app/skills',
            label: 'Browse Skills',
            desc: 'View and manage all tracked skills',
            icon: BookOpen,
          },
          {
            to: '/app/timer',
            label: 'Start Session',
            desc: 'Begin a timed practice session',
            icon: Clock,
          },
          {
            to: '/app/skills/new',
            label: 'New Skill',
            desc: 'Add a new skill to track',
            icon: PlusCircle,
          },
        ].map(({ to, label, desc, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="panel p-4 flex items-start gap-3 transition-all duration-200"
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border-hi)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border)';
            }}
          >
            <div
              className="shrink-0 p-2 mt-0.5"
              style={{
                background: 'var(--color-accent-dim)',
                border: '1px solid var(--color-border)',
              }}
            >
              <Icon size={14} style={{ color: 'var(--color-accent)' }} />
            </div>
            <div>
              <p
                className="text-sm font-medium"
                style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-text)' }}
              >
                {label}
              </p>
              <p
                className="text-xs mt-0.5"
                style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}
              >
                {desc}
              </p>
            </div>
          </Link>
        ))}
      </div>

    </div>
  );
}
